import { useCallback, useMemo } from "react";
import {
  EnrichedSupplierForDelivery,
  ManualDeliveryOptionEntry,
  SelectedDeliveryOption,
} from "../../types";
import { MANUAL_DELIVERY_OPTION_RADIO_VALUE } from "../../utils/deliveryOptionSelectionUtils";

interface UseEstimateSplitSelectionParams {
  supplier: EnrichedSupplierForDelivery;
  selection: SelectedDeliveryOption | undefined;
  onSelectionChange: (selection: SelectedDeliveryOption | undefined) => void;
}

export function useEstimateSplitSelection({
  supplier,
  selection,
  onSelectionChange,
}: UseEstimateSplitSelectionParams) {
  const selectedDeliveryOptionId =
    selection?.customerAction === "confirm" &&
    selection?.deliveryDetails?.selectionType === "option"
      ? selection.deliveryDetails.selectedDeliveryOptionId
      : undefined;

  const isManualSelected =
    selection?.customerAction === "confirm" &&
    selection?.deliveryDetails?.selectionType === "manual";

  const isBuyTimeSelected = selection?.customerAction === "buytime";

  const radioValue = useMemo(() => {
    if (isManualSelected) return MANUAL_DELIVERY_OPTION_RADIO_VALUE;
    if (selectedDeliveryOptionId) return selectedDeliveryOptionId;
    return "";
  }, [isManualSelected, selectedDeliveryOptionId]);

  const handleDeliveryOptionSelect = useCallback(
    (deliveryOptionId: string) => {
      const optionExists = supplier.deliveryOptions.some(
        (opt) => opt._id === deliveryOptionId,
      );
      if (!optionExists) return;

      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "option",
          selectedDeliveryOptionId: deliveryOptionId,
        },
      });
    },
    [supplier.supplierId, supplier.deliveryOptions, onSelectionChange],
  );

  const handleManualOptionSelect = useCallback(() => {
    if (isManualSelected) return;

    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction: "confirm",
      deliveryDetails: {
        selectionType: "manual",
        manualEntry: selection?.deliveryDetails?.manualEntry || {
          cpt: "",
          deliveryCharge: 0,
        },
      },
    });
  }, [isManualSelected, supplier.supplierId, selection, onSelectionChange]);

  const handleHold = useCallback(() => {
    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction: selection?.customerAction === "hold" ? "confirm" : "hold",
    });
  }, [supplier.supplierId, selection, onSelectionChange]);

  const handleCancel = useCallback(() => {
    onSelectionChange({
      supplierId: supplier.supplierId,
      customerAction:
        selection?.customerAction === "cancel" ? "confirm" : "cancel",
    });
  }, [supplier.supplierId, selection, onSelectionChange]);

  const handleManualEntryCancel = useCallback(
    (isExpired: boolean) => {
      if (isExpired) return;
      onSelectionChange(undefined);
    },
    [onSelectionChange],
  );

  const handleManualOptionUpdate = useCallback(
    (entry: ManualDeliveryOptionEntry) => {
      onSelectionChange({
        supplierId: supplier.supplierId,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "manual",
          manualEntry: entry,
        },
      });
    },
    [supplier.supplierId, onSelectionChange],
  );

  const isDeliveryOptionSelected = useCallback(
    (deliveryOptionId: string) =>
      selectedDeliveryOptionId === deliveryOptionId,
    [selectedDeliveryOptionId],
  );

  return {
    selectedDeliveryOptionId,
    isManualSelected,
    isBuyTimeSelected,
    radioValue,
    handleDeliveryOptionSelect,
    handleManualOptionSelect,
    handleHold,
    handleCancel,
    handleManualEntryCancel,
    handleManualOptionUpdate,
    isDeliveryOptionSelected,
  };
}
